import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import AdminService from "../../../api/services/adminService";
import { hasPermission, PERMISSION_KEYS } from "../../../utils/permissions";
import MasterTraderConfigModal from "./MasterTraderConfigModal";
import MasterFollowersModal from "./MasterFollowersModal";
import CopyTradingOpsPanels from "./CopyTradingOpsPanels";
import "../Home/Homepage.css";
import "./CopyTradingManagementPage.css";
import "./CopyTradingOpsPanels.css";

const DEFAULT_PAGE_LIMIT = 20;
const PAGE_LIMIT_OPTIONS = [20, 50, 100, 500, 1000];

const TABS = [
  { key: "master", label: "Master Traders", role: "master", icon: "fa-star" },
  { key: "none", label: "Promote a user", role: "none", icon: "fa-user-plus" },
];

const SECTION_TABS = [
  { key: "traders", label: "Traders", icon: "fa-users" },
  { key: "risk", label: "Risk flags", icon: "fa-shield-alt" },
  { key: "settlements", label: "Settlements", icon: "fa-file-invoice-dollar" },
  { key: "alerts", label: "Alerts", icon: "fa-bell" },
  { key: "surveillance", label: "Surveillance", icon: "fa-eye" },
  { key: "reconciliation", label: "Reconciliation", icon: "fa-link" },
  { key: "snapshots", label: "Snapshots", icon: "fa-camera" },
];

const riskPills = (state) => {
  if (!state || typeof state !== "object") return [];
  const out = [];
  if (state.dailyLossBlocked) out.push("Daily loss");
  if (state.totalStopBlocked) out.push("Total stop");
  if (state.walletReserveBlocked) out.push("Reserve");
  return out;
};

const KYC_LABELS = {
  0: "Not Submitted",
  1: "Pending",
  2: "Verified",
  3: "Rejected",
};

const KYC_BADGE = {
  0: "badge--pending",
  1: "badge--pending",
  2: "badge--verified",
  3: "badge--rejected",
};

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
};

const displayName = (row) => {
  const name = [row?.first_name, row?.last_name].filter(Boolean).join(" ").trim();
  return name || "—";
};

const isKycVerified = (row) => Number(row?.kyc_verified) === 2;

const CopyTradingManagementPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("master");
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ master: 0, none: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [searchDebounced, setSearchDebounced] = useState("");
  const [pagination, setPagination] = useState({
    page: 1,
    limit: DEFAULT_PAGE_LIMIT,
    total: 0,
    totalPages: 0,
  });
  const [confirmAction, setConfirmAction] = useState(null);
  const [saving, setSaving] = useState(false);
  const [configMaster, setConfigMaster] = useState(null);
  const [followersMaster, setFollowersMaster] = useState(null);
  const [configMap, setConfigMap] = useState({});
  const [section, setSection] = useState("traders");
  const [demoteReason, setDemoteReason] = useState("");
  const [demoteForce, setDemoteForce] = useState(false);
  const [windDownResult, setWindDownResult] = useState(null);

  const tabConfig = TABS.find((t) => t.key === activeTab) || TABS[0];

  const buildParams = useCallback(
    (pageNum = pagination.page, limitNum = pagination.limit, role = tabConfig.role) => {
      const params = {
        role,
        page: pageNum,
        limit: limitNum,
      };
      if (searchDebounced.trim()) params.search = searchDebounced.trim();
      return params;
    },
    [pagination.page, pagination.limit, tabConfig.role, searchDebounced]
  );

  const fetchCounts = useCallback(async () => {
    const searchTerm = searchDebounced.trim();
    const results = await Promise.all(
      TABS.map((tab) => {
        const params = { role: tab.role, page: 1, limit: 1 };
        if (searchTerm) params.search = searchTerm;
        return AdminService.getMasterTraders(params);
      })
    );
    const next = {};
    TABS.forEach((tab, i) => {
      next[tab.key] = results[i]?.pagination?.total ?? 0;
    });
    setCounts(next);
  }, [searchDebounced]);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError("");
    const res = await AdminService.getMasterTraders(buildParams());
    setLoading(false);
    if (res?.success) {
      setRows(Array.isArray(res.data) ? res.data : []);
      const pag = res.pagination || {};
      setPagination((prev) => ({
        ...prev,
        total: pag.total ?? 0,
        totalPages: pag.total_pages ?? pag.totalPages ?? 0,
      }));
      if (pag.total != null) {
        setCounts((c) => ({ ...c, [activeTab]: pag.total }));
      }
    } else {
      setError(res?.message || "Failed to load copy trading users.");
      setRows([]);
    }
  }, [buildParams, activeTab]);

  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setPagination((p) => ({ ...p, page: 1 }));
  }, [activeTab, searchDebounced, pagination.limit]);

  useEffect(() => {
    if (section !== "traders") return;
    fetchList();
  }, [fetchList, section]);

  useEffect(() => {
    if (section !== "traders") return;
    fetchCounts();
  }, [fetchCounts, section]);

  useEffect(() => {
    if (activeTab !== "master" || !rows.length) return undefined;
    let cancelled = false;
    const hydrateConfigs = async () => {
      const masters = rows.filter((r) => r.is_master_account && r._id);
      if (!masters.length) return;
      const entries = await Promise.all(
        masters.map(async (m) => {
          const res = await AdminService.getMasterTraderConfiguration(m._id);
          return [m._id, !!(res?.success && res.data)];
        })
      );
      if (!cancelled) {
        setConfigMap((prev) => ({ ...prev, ...Object.fromEntries(entries) }));
      }
    };
    hydrateConfigs();
    return () => {
      cancelled = true;
    };
  }, [rows, activeTab]);

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > pagination.totalPages) return;
    setPagination((p) => ({ ...p, page: newPage }));
  };

  const handlePageLimitChange = (e) => {
    setPagination((p) => ({
      ...p,
      limit: Number(e.target.value) || DEFAULT_PAGE_LIMIT,
      page: 1,
    }));
  };

  const openConfirm = (row, isMasterAccount) => {
    if (isMasterAccount && !isKycVerified(row)) {
      toast.error("Promotion is limited to KYC-verified users.");
      return;
    }
    setConfirmAction({ row, isMasterAccount });
    setDemoteReason("");
    setDemoteForce(false);
    setWindDownResult(null);
  };

  const closeConfirm = () => {
    if (saving) return;
    setConfirmAction(null);
    setWindDownResult(null);
  };

  const submitRoleChange = async () => {
    const action = confirmAction;
    if (!action?.row?._id) return;
    setSaving(true);
    try {
      const extras = action.isMasterAccount
        ? {}
        : { reason: demoteReason.trim(), force: demoteForce };
      const res = await AdminService.updateMasterTradeAccount(
        action.row._id,
        action.isMasterAccount,
        extras
      );
      const data = res?.data && typeof res.data === "object" ? res.data : {};
      const windDownRan = !!data.windDownRan;
      if (res?.success || windDownRan) {
        if (windDownRan) {
          setWindDownResult(data);
          const failed = (data.followerResults || []).some(
            (r) => String(r.status || "").toUpperCase() === "FAILED"
          );
          const stillMaster = data.isMasterAccount !== false;
          if (failed || stillMaster) {
            toast.error(
              res?.message ||
                "Wind-down halted before demotion. Inspect follower results, then retry or force."
            );
          } else {
            toast.success(res?.message || "Master wind-down completed.");
            setConfirmAction(null);
            setWindDownResult(null);
          }
        } else {
          toast.success(res?.message || "Master trade account updated.");
          setConfirmAction(null);
          setWindDownResult(null);
        }
        const nextIsMaster = data.isMasterAccount != null
          ? !!data.isMasterAccount
          : action.isMasterAccount;
        setRows((prev) =>
          prev.map((r) =>
            r._id === action.row._id ? { ...r, is_master_account: nextIsMaster } : r
          )
        );
        await Promise.all([fetchList(), fetchCounts()]);
        if (action.isMasterAccount && nextIsMaster) {
          setConfigMaster(action.row);
        }
      } else {
        toast.error(res?.message || "Failed to update master trade account.");
      }
    } catch (err) {
      toast.error(err?.message || "Failed to update master trade account.");
    } finally {
      setSaving(false);
    }
  };

  const pageStart = rows.length === 0 ? 0 : (pagination.page - 1) * pagination.limit + 1;
  const pageEnd = rows.length === 0 ? 0 : (pagination.page - 1) * pagination.limit + rows.length;
  const isPromoteTab = activeTab === "none";
  const canViewTrader =
    hasPermission(PERMISSION_KEYS.USERS) || hasPermission(PERMISSION_KEYS.KYC);

  return (
    <main className="dashboard-main dashboard-subpage">
      <section className="dashboard-hero">
        <div className="dashboard-hero__text">
          <p className="dashboard-hero__breadcrumb">
            <Link to="/dashboard/homepage">Dashboard</Link>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <span>Copy Trading Management</span>
          </p>
          <h1 className="dashboard-hero__title">Copy Trading Management</h1>
          <p className="dashboard-hero__subtitle">
            Promote and configure masters, enforce risk, settle commission, and inspect copy-trading ops.
          </p>
        </div>
        <div className="dashboard-hero__icon">
          <i className="fas fa-clone" />
        </div>
      </section>

      <div className="dashboard-body">
        <section className="dashboard-activity-section">
          <div className="dashboard-activity-header copy-trading-header">
            <div className="copy-trading-header__left">
              <div className="dashboard-activity-header__icon">
                <i className="fas fa-clone" />
              </div>
              <div>
                <h2 className="dashboard-activity-header__title">Manage Master Traders</h2>
                <p className="copy-trading-header__hint">
                  Master accounts need a configuration before followers can copy their trades.
                </p>
              </div>
            </div>
            <div className="copy-trading-header__actions">
              {section === "traders" && (
                <>
              <span className="copy-trading-stat">
                <strong>{rows.length}</strong> on page
                {pagination.total > 0 && (
                  <>
                    {" "}
                    (<strong>{pagination.total}</strong> total)
                  </>
                )}
              </span>
              {pagination.totalPages > 1 && (
                <span className="copy-trading-stat">
                  Page <strong>{pagination.page}</strong> / <strong>{pagination.totalPages}</strong>
                </span>
              )}
              <button
                type="button"
                className="copy-trading-refresh-btn"
                onClick={() => {
                  fetchList();
                  fetchCounts();
                }}
                disabled={loading}
                title="Refresh"
              >
                <i className={`fas fa-sync-alt ${loading ? "fa-spin" : ""}`} />
                Refresh
              </button>
                </>
              )}
            </div>
          </div>

          <div className="copy-trading-section-tabs">
            <Link to="/dashboard/copy-trading/master-applications" className="copy-trading-section-tab">
              <i className="fas fa-inbox" /> Master Applications
            </Link>
            {SECTION_TABS.map((tab) => (
              <button
                key={tab.key}
                type="button"
                className={`copy-trading-section-tab ${
                  section === tab.key ? "copy-trading-section-tab--active" : ""
                }`}
                onClick={() => setSection(tab.key)}
              >
                <i className={`fas ${tab.icon}`} />
                {tab.label}
              </button>
            ))}
          </div>

          {section !== "traders" ? (
            <CopyTradingOpsPanels section={section} />
          ) : (
            <>
          <div className="copy-trading-filters">
            <input
              type="text"
              placeholder="Search email, UUID, mobile, name, referral code…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="copy-trading-search"
            />
            <label className="copy-trading-page-limit">
              Rows per page
              <select
                value={pagination.limit}
                onChange={handlePageLimitChange}
                className="copy-trading-page-limit__select"
              >
                {PAGE_LIMIT_OPTIONS.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="copy-trading-tabs">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                type="button"
                className={`copy-trading-tab ${activeTab === tab.key ? "copy-trading-tab--active" : ""}`}
                onClick={() => setActiveTab(tab.key)}
              >
                <i className={`fas ${tab.icon}`} />
                {tab.label}
                <span className="copy-trading-tab__count">{counts[tab.key] ?? 0}</span>
              </button>
            ))}
          </div>

          {error && (
            <div className="copy-trading-error">
              <i className="fas fa-exclamation-circle" /> {error}
            </div>
          )}

          {isPromoteTab && (
            <p className="copy-trading-note">
              <i className="fas fa-info-circle" /> Promotion is limited to KYC-verified users in this
              screen. Search for a regular account, then promote them to a master trader.
            </p>
          )}

          <div className="dashboard-content-inner copy-trading-content">
            <div className="table-wrap copy-trading-table-wrap">
              <table className="data-table copy-trading-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>UUID</th>
                    <th>Contact</th>
                    <th>KYC</th>
                    <th>Status</th>
                    <th>Referral</th>
                    <th>Signup</th>
                    <th>Role</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && rows.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="copy-trading-table__empty">
                        <i className="fas fa-spinner fa-spin" /> Loading…
                      </td>
                    </tr>
                  ) : rows.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="copy-trading-table__empty">
                        {isPromoteTab
                          ? "No regular accounts match your search."
                          : "No master traders yet. Use Promote a user to convert an account."}
                      </td>
                    </tr>
                  ) : (
                    rows.map((row) => {
                      const kyc = Number(row.kyc_verified);
                      const verified = isKycVerified(row);
                      const isMaster = !!row.is_master_account;
                      const configKnown = configMap[row._id];
                      const configured = configKnown === true;
                      return (
                        <tr key={row._id}>
                          <td>
                            <div className="copy-trading-user">
                              <strong>{displayName(row)}</strong>
                              <span>{row.email_masked || "—"}</span>
                            </div>
                          </td>
                          <td>
                            <code className="copy-trading-uuid">{row.uuid || "—"}</code>
                          </td>
                          <td>{row.mobile_masked || "—"}</td>
                          <td>
                            <span className={`badge ${KYC_BADGE[kyc] || "badge--pending"}`}>
                              {KYC_LABELS[kyc] ?? "—"}
                            </span>
                          </td>
                          <td>
                            <span
                              className={`badge ${
                                String(row.account_status || row.status || "").toLowerCase() ===
                                "active"
                                  ? "badge--active"
                                  : "badge--inactive"
                              }`}
                            >
                              {row.account_status || row.status || "—"}
                            </span>
                          </td>
                          <td>{row.referral_code || "—"}</td>
                          <td>{formatDate(row.signup_date)}</td>
                          <td>
                            <span
                              className={`copy-trading-role ${
                                isMaster
                                  ? "copy-trading-role--master"
                                  : "copy-trading-role--none"
                              }`}
                            >
                              {isMaster ? "Master" : "Regular"}
                            </span>
                            {isMaster && (
                              <span
                                className={`copy-trading-config-flag ${
                                  configKnown === true
                                    ? "copy-trading-config-flag--yes"
                                    : configKnown === false
                                    ? "copy-trading-config-flag--no"
                                    : "copy-trading-config-flag--pending"
                                }`}
                              >
                                {configKnown === true
                                  ? "Configured"
                                  : configKnown === false
                                  ? "Needs config"
                                  : "Checking…"}
                              </span>
                            )}
                            {isMaster && riskPills(row.riskState).length > 0 && (
                              <div className="copy-trading-risk-pills">
                                {riskPills(row.riskState).map((label) => (
                                  <span key={label} className="copy-trading-risk-pill">
                                    {label}
                                  </span>
                                ))}
                              </div>
                            )}
                          </td>
                          <td className="copy-trading-table__actions">
                            {canViewTrader && (
                              <button
                                type="button"
                                className="copy-trading-view-btn"
                                onClick={() => navigate(`/dashboard/trader/${row._id}`)}
                                title="View trader details"
                              >
                                <i className="fas fa-eye" /> View
                              </button>
                            )}
                            {isMaster ? (
                              <>
                                <button
                                  type="button"
                                  className="copy-trading-followers-btn"
                                  onClick={() => setFollowersMaster(row)}
                                  title="View followers, P&L, trades, and copied trades"
                                >
                                  <i className="fas fa-chart-line" /> Activity
                                </button>
                                <button
                                  type="button"
                                  className="copy-trading-config-btn"
                                  onClick={() => setConfigMaster(row)}
                                  disabled={
                                    saving ||
                                    String(row.account_status || "").toLowerCase() === "blocked"
                                  }
                                  title={
                                    String(row.account_status || "").toLowerCase() === "blocked"
                                      ? "Cannot configure a blocked user"
                                      : configured
                                      ? "Edit master configuration"
                                      : "Create master configuration"
                                  }
                                >
                                  <i className="fas fa-sliders-h" />{" "}
                                  {configured ? "Edit config" : "Configure"}
                                </button>
                                <button
                                  type="button"
                                  className="copy-trading-demote-btn"
                                  onClick={() => openConfirm(row, false)}
                                  disabled={saving}
                                >
                                  <i className="fas fa-user-minus" /> Revert
                                </button>
                              </>
                            ) : (
                              <button
                                type="button"
                                className="copy-trading-promote-btn"
                                onClick={() => openConfirm(row, true)}
                                disabled={saving || !verified}
                                title={
                                  verified
                                    ? "Promote to master trade account"
                                    : "KYC must be verified before promotion"
                                }
                              >
                                <i className="fas fa-user-plus" /> Promote
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {!loading && pagination.total > 0 && (
              <div className="copy-trading-pagination">
                <span className="copy-trading-pagination__info">
                  Showing {pageStart}–{pageEnd} of {pagination.total}
                  {pagination.totalPages > 1 && (
                    <>
                      {" "}
                      · Page {pagination.page} of {pagination.totalPages}
                    </>
                  )}
                </span>
                {pagination.totalPages > 1 && (
                  <div className="copy-trading-pagination__buttons">
                    <button
                      type="button"
                      className="copy-trading-pagination__btn"
                      disabled={pagination.page <= 1}
                      onClick={() => handlePageChange(pagination.page - 1)}
                    >
                      <i className="fas fa-chevron-left" /> Prev
                    </button>
                    <button
                      type="button"
                      className="copy-trading-pagination__btn"
                      disabled={pagination.page >= pagination.totalPages}
                      onClick={() => handlePageChange(pagination.page + 1)}
                    >
                      Next <i className="fas fa-chevron-right" />
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
            </>
          )}
        </section>
      </div>

      {confirmAction && (
        <div
          className="copy-trading-modal-overlay"
          onClick={closeConfirm}
          role="presentation"
        >
          <div
            className={`copy-trading-modal ${
              confirmAction.isMasterAccount ? "" : "copy-trading-modal--wide"
            }`}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="copy-trading-confirm-title"
          >
            <h3 id="copy-trading-confirm-title" className="copy-trading-modal__title">
              <i
                className={`fas ${
                  confirmAction.isMasterAccount ? "fa-user-plus" : "fa-user-minus"
                }`}
              />
              {confirmAction.isMasterAccount
                ? "Promote to master trader"
                : "Revert to normal account"}
            </h3>
            <p className="copy-trading-modal__text">
              {confirmAction.isMasterAccount ? (
                <>
                  Convert{" "}
                  <strong>
                    {displayName(confirmAction.row) !== "—"
                      ? displayName(confirmAction.row)
                      : confirmAction.row.email_masked || confirmAction.row.uuid}
                  </strong>{" "}
                  to a master trade account? Other users will later be able to follow and
                  auto-copy this trader's trades.
                </>
              ) : (
                <>
                  Revert{" "}
                  <strong>
                    {displayName(confirmAction.row) !== "—"
                      ? displayName(confirmAction.row)
                      : confirmAction.row.email_masked || confirmAction.row.uuid}
                  </strong>{" "}
                  to a normal account. If they still have ACTIVE or PAUSED followers, this starts a
                  wind-down: new follows and opening mirrors stop immediately, each follower is closed
                  or detached per their preference, and commission is crystallized. Zero-follower
                  masters demote instantly.
                </>
              )}
            </p>
            {!confirmAction.isMasterAccount && (
              <>
                <label className="copy-trading-modal__field">
                  Reason (audit trail)
                  <textarea
                    rows={2}
                    value={demoteReason}
                    onChange={(e) => setDemoteReason(e.target.value)}
                    placeholder="Optional"
                    disabled={saving}
                  />
                </label>
                <label className="copy-trading-modal__check">
                  <input
                    type="checkbox"
                    checked={demoteForce}
                    onChange={(e) => setDemoteForce(e.target.checked)}
                    disabled={saving}
                  />
                  <span>
                    Force complete if a follower close fails (leaves the failure on record and still
                    demotes).
                  </span>
                </label>
              </>
            )}
            {windDownResult && (
              <div className="copy-trading-wind-results">
                Wind-down {windDownResult.isMasterAccount === false ? "completed" : "in progress / halted"}.
                {Array.isArray(windDownResult.followerResults) &&
                windDownResult.followerResults.length ? (
                  <ul>
                    {windDownResult.followerResults.map((fr) => (
                      <li key={fr.followerId}>
                        {fr.followerId}: {fr.status || "—"}
                        {fr.preference ? ` · ${fr.preference}` : ""}
                        {fr.error ? ` · ${fr.error}` : ""}
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            )}
            <div className="copy-trading-modal__actions">
              <button
                type="button"
                className="copy-trading-modal__cancel"
                onClick={closeConfirm}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="button"
                className={
                  confirmAction.isMasterAccount
                    ? "copy-trading-promote-btn"
                    : "copy-trading-demote-btn"
                }
                onClick={submitRoleChange}
                disabled={saving}
              >
                {saving ? (
                  <>
                    <i className="fas fa-spinner fa-spin" /> Saving…
                  </>
                ) : confirmAction.isMasterAccount ? (
                  <>
                    <i className="fas fa-user-plus" /> Promote
                  </>
                ) : (
                  <>
                    <i className="fas fa-user-minus" /> {windDownResult ? "Retry wind-down" : "Revert"}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      {configMaster && (
        <MasterTraderConfigModal
          key={configMaster._id}
          master={configMaster}
          onClose={() => setConfigMaster(null)}
          onSaved={() =>
            setConfigMap((prev) => ({ ...prev, [configMaster._id]: true }))
          }
        />
      )}
      {followersMaster && (
        <MasterFollowersModal
          key={followersMaster._id}
          master={followersMaster}
          onClose={() => setFollowersMaster(null)}
        />
      )}
    </main>
  );
};

export default CopyTradingManagementPage;
